import {useMemo, useRef} from 'react';
import type {MediaAdapter, MediaKind} from '@forecast-it/design-system';
import DirectApi from '../../../../../directApi';
import Util, {MAX_FILES_PER_UPLOAD, validateUploadFile} from '../../../util/util';
import {MAX_PREVIEW_FILE_SIZE} from '../../../../../constants';
import {createToast} from '../../toasts/another-toast/toaster';
import CreateFileMutation from '../../../../../mutations/create_file_mutation';
import {fromFileReference, toFileReference} from './fileReference';
import {trackInlineMediaResolveFailed, trackInlineMediaUpload} from '../../../../../tracking/amplitude/editorEvents';
import type {IntlShape} from 'react-intl';

/**
 * The editor's own contract, aliased for readability at the call sites here.
 *
 * This was a local structural widening of `ImageAdapter` while the installed
 * design-system predated the non-image kinds. Now that the dependency carries the
 * real type, use it: a drift between our adapter and what the editor actually
 * calls should be a type error rather than something that only shows up at
 * runtime.
 */
export type InlineMediaAdapter = MediaAdapter;

/** @deprecated Use InlineMediaAdapter. */
export type InlineImageAdapter = InlineMediaAdapter;

export type {MediaKind};

/**
 * Largest file that gets an inline player rather than a chip.
 *
 * A chip streams nothing until it is clicked, so it needs no limit beyond the
 * ordinary file cap. A player is different: `preload="metadata"` reaches the
 * endpoint for every viewer who opens the task, and anyone who presses play pulls
 * the whole file through the graph process. 200MB is comfortably several minutes
 * of screen recording, which is the case that motivated inline video at all.
 *
 * Over-cap files are **not** rejected — `classify` returns `'file'` and they
 * render as chips, so nothing is blocked and the reference stays at the point in
 * the text that mentions it. Only the embedded player is withheld.
 *
 * A constant rather than a feature flag: the number should move once there is
 * real data on what customers upload, and moving it is a one-line change.
 */
export const INLINE_PLAYBACK_MAX_BYTES = 200 * 1024 * 1024;

/**
 * Largest image that gets embedded inline rather than becoming a chip.
 *
 * Images were originally exempt from every cap, which had it backwards: an inline
 * `<img>` is the *least* staged of the three. A player fetches metadata on open and
 * the body only when someone presses play, whereas an image is pulled in full by
 * every viewer who opens the task, with no click and no opt-in — `loading="lazy"`
 * only defers it until it scrolls into view. So a 900MB PNG was embedded while a
 * 201MB video beside it was downgraded to a chip that transfers nothing.
 *
 * Set to the preview cap the product already uses. `MAX_PREVIEW_FILE_SIZE` is what
 * decides whether the Files list will even render a thumbnail, so an image too large
 * to thumbnail is now also too large to inline — one policy rather than two, and
 * strictly the more conservative of them.
 */
export const INLINE_IMAGE_MAX_BYTES = MAX_PREVIEW_FILE_SIZE;

const KIND_BY_MIME_PREFIX: ReadonlyArray<[string, MediaKind]> = [
	['image/', 'image'],
	['video/', 'video'],
	['audio/', 'audio'],
];

/**
 * The media kind a MIME type implies, for analytics.
 *
 * Deliberately not `classify`'s answer: that one degrades a large image or video to a
 * chip, which is the right rendering decision but the wrong label on an upload event —
 * "images fail more than videos" is the question the property has to be able to answer.
 */
const kindOfMimeType = (mimeType: string): MediaKind =>
	KIND_BY_MIME_PREFIX.find(([prefix]) => mimeType.startsWith(prefix))?.[1] ?? 'file';

/**
 * Connects the editor's media plugins to task file storage.
 *
 * Uploads become ordinary files on the task, so they inherit its permissions and
 * cascade behaviour. What is written into the document is a reference, not the
 * serving URL — see fileReference for why — and the credential and privacy
 * attributes are applied here at render time because the storage format cannot
 * carry them.
 *
 * `getOwnFileIds` reports the files already on this task, and is a getter rather
 * than a snapshot on purpose — see `adoptReference` below.
 *
 * Exposed as a plain factory as well as a hook because every task-modal surface
 * that needs it is a class component. The modal builds one adapter and hands it
 * to all three; see `useInlineMediaAdapter` below for the hook form.
 *
 * **Hold the returned object's identity stable for as long as `taskId` is
 * unchanged.** `RichTextEditor` rebuilds its plugin list when the adapter
 * changes, which re-deserializes the whole document — and it must never be
 * keyed on the file set, because `create_file_mutation` RANGE_ADDs onto
 * `Task_files`, so every successful inline upload would rebuild the editor at
 * the moment the user just inserted a file.
 */
export const createInlineMediaAdapter = (
	taskId: string,
	getOwnFileIds: () => Set<string>,
	intl: IntlShape,
	/**
	 * Opens one of the task's files. Supplied by the modal rather than implemented
	 * here, because deciding between a preview and a download needs the file row —
	 * whose name is authoritative, where the name stored on the node can be stale —
	 * and because the modal is what owns the modal stack. Omitted, a chip is inert
	 * but still readable.
	 */
	openFile?: (fileId: string) => void
): InlineMediaAdapter => {
	// `resolve` is a render-path function: the editor calls it for every media element
	// on every render, so a single unresolvable reference would otherwise raise one
	// analytics event per keystroke. Reporting each distinct reference once per adapter
	// keeps the signal ("this reference is broken") without the volume.
	const reportedResolveFailures = new Set<string>();

	// Both toasts go through formatMessage. The rejection message previously built a
	// raw template string, so that path was already untranslated for every
	// non-English customer; it is fixed here rather than left as the odd one out.
	const reportRejected = (name: string, reason: string): void => {
		createToast({
			duration: 5000,
			message: intl.formatMessage({id: 'inline_media.could_not_be_added'}, {fileName: name, reason}),
		});
	};

	const reportDegradedToChip = (name: string): void => {
		createToast({
			duration: 5000,
			message: intl.formatMessage({id: 'inline_media.too_large_to_show_here'}, {fileName: name}),
		});
	};

	const uploadBlob = (file: File): Promise<string | null> =>
		new Promise((resolve, reject) => {
			// The batch uploader normally enforces these, but inline insertion bypasses
			// it to get a file id back synchronously, so the same rule set is applied
			// here rather than duplicated.
			const rejection = validateUploadFile({name: file.name, mimeType: file.type, file});
			if (rejection) {
				reportRejected(file.name, rejection);
				trackInlineMediaUpload('failed', {kind: kindOfMimeType(file.type), mimeType: file.type, sizeBytes: file.size});
				resolve(null);
				return;
			}

			Util.CommitMutation(
				CreateFileMutation,
				// The File must NOT go in here. Relay JSON-serialises variables, so it
				// would arrive as {} and create a row with no bytes behind it.
				{taskId, name: file.name, mimeType: file.type, size: file.size, file: undefined},
				(response: {createFile?: {error?: string; file?: {node: {id: string}}}}) => {
					const created = response?.createFile;
					const createdId = created?.error ? undefined : created?.file?.node.id;
					// A refusal resolves rather than rejects, so it is invisible to the caller's
					// error handling — the user is simply left with a gap where the file should
					// be, and nothing else records that it happened.
					trackInlineMediaUpload(createdId ? 'succeeded' : 'failed', {
						kind: kindOfMimeType(file.type),
						mimeType: file.type,
						sizeBytes: file.size,
					});
					resolve(createdId ? toFileReference(createdId) : null);
				},
				true,
				// Uploadables is what actually transfers the bytes, via multipart.
				{file},
				(transaction: unknown, onFailure: (t: unknown) => boolean) => {
					// A transient or CORS failure schedules its own retry and says so via
					// the return value — leave the promise pending so the retry settles it.
					if (onFailure(transaction)) return;
					trackInlineMediaUpload('failed', {
						kind: kindOfMimeType(file.type),
						mimeType: file.type,
						sizeBytes: file.size,
					});
					reject(new Error(`Inline media upload failed: ${file.name}`));
				}
			);
		});

	return {
		maxFilesPerAction: MAX_FILES_PER_UPLOAD,

		upload: uploadBlob,

		/**
		 * Which element this file becomes, or null to refuse it.
		 *
		 * `validateUploadFile` runs first, so the existing rules — the file-size cap,
		 * the blacklist, name length, extension required — are enforced in one place
		 * rather than duplicated, and a refusal costs no upload at all. Then the MIME
		 * prefix, then the playback cap.
		 *
		 * The kind is decided here and baked into the stored entity, so raising the cap
		 * later does not retroactively turn an existing chip into a player. That is
		 * deliberate: the document says what it is, and nothing re-renders differently
		 * after the fact.
		 */
		classify: (file: File): MediaKind | null => {
			const rejection = validateUploadFile({name: file.name, mimeType: file.type, file});
			if (rejection) {
				reportRejected(file.name, rejection);
				return null;
			}

			const matched = KIND_BY_MIME_PREFIX.find(([prefix]) => file.type.startsWith(prefix));
			if (!matched) {
				// Everything without a player — documents, archives, anything unknown —
				// is a chip. There is nothing to render inline, and the filename is the
				// useful information.
				return 'file';
			}

			const [, kind] = matched;
			if (kind === 'image') {
				// Capped, not exempt — and on a lower number than a player, because an image
				// transfers in full for every viewer who opens the task rather than only for
				// those who press play.
				if (file.size > INLINE_IMAGE_MAX_BYTES) {
					reportDegradedToChip(file.name);
					return 'file';
				}
				return 'image';
			}

			if (file.size > INLINE_PLAYBACK_MAX_BYTES) {
				// Degrade, never reject: the file still uploads and still sits inline at
				// the point in the text that mentions it. Only the player is withheld,
				// because a file this large is served in full to every viewer who plays it.
				reportDegradedToChip(file.name);
				return 'file';
			}
			return kind;
		},

		resolve: (reference: string, kind: MediaKind = 'image') => {
			const fileId = fromFileReference(reference);
			if (!fileId && !reportedResolveFailures.has(reference)) {
				// An empty src renders as a broken image sitting in the customer's
				// description. No request is made, so nothing fails that we could otherwise
				// see, and nobody hears about it until someone notices the gap.
				reportedResolveFailures.add(reference);
				trackInlineMediaResolveFailed(kind);
			}
			return {
				src: fileId ? DirectApi.fileSrc(fileId) : '',
				// Sends cookies so the authenticated asset endpoint serves the file.
				crossOrigin: 'use-credentials' as const,
				// Keeps the src out of session-replay recordings, so the replay player
				// does not hammer the backend with unauthenticated requests. Required on
				// <video> and <source> as well as <img>.
				'data-private': true,
				// Only meaningful on an <img>. A media element has its own preload
				// attribute and browsers ignore `loading` on it, so setting it there
				// would put a misleading attribute in the DOM.
				...(kind === 'image' ? {loading: 'lazy' as const} : {}),
			};
		},

		/**
		 * Decides whether an incoming <img> src is ours, and what to store for it.
		 *
		 * Accepts our own reference scheme, and a resolved file_stream URL — which
		 * is what HTML copied out of a rendered description carries — normalising
		 * the latter back to a reference so the resolved form is never stored.
		 * Anything else is a third-party image and is rejected.
		 */
		recogniseSrc: (src: string) => {
			if (fromFileReference(src)) return src;

			const base = DirectApi.fileSrc('');
			if (src.startsWith(base)) {
				const fileId = src.slice(base.length).split(/[?#]/)[0];
				return fileId ? toFileReference(fileId) : null;
			}
			return null;
		},

		/**
		 * Turns the stored reference back into a file id and hands it to the modal.
		 *
		 * Nothing is resolved to a URL here: the chip must not hold a fetchable address
		 * for a file the viewer may not be entitled to, and the modal already knows how
		 * to present one it can see.
		 */
		openReference: openFile
			? (reference: string) => {
					const fileId = fromFileReference(reference);
					if (fileId) {
						openFile(fileId);
					}
			  }
			: undefined,

		/**
		 * Makes an incoming reference belong to this task.
		 *
		 * Rich text copied from another task carries that task's file references. Left
		 * alone, the file would vanish when the original is removed, and may not even be
		 * readable by this task's viewers if the two sit in different projects.
		 * Re-uploading keeps every task's inline media self-contained; a reference that
		 * cannot be fetched is dropped rather than stored broken.
		 *
		 * The ids are read here, at call time, and never captured. Plate installs this
		 * override through `withOverrides` when the editor is *constructed*, so a later
		 * adapter's closure never replaces this one — a snapshot taken at construction
		 * would stay frozen for the editor's whole life. With one, copying a file
		 * inserted during this session and pasting it again in the same task would look
		 * foreign and be re-uploaded, duplicating a file the task already owns; and if
		 * `task.files` had not resolved when the editor mounted, every re-paste would
		 * duplicate.
		 */
		adoptReference: async (reference: string, hints?: {name?: string; mimeType?: string}) => {
			const fileId = fromFileReference(reference);
			if (!fileId) return null;
			if (getOwnFileIds().has(fileId)) return reference;

			try {
				const response = await fetch(DirectApi.fileSrc(fileId), {credentials: 'include'});
				if (!response.ok) return null;
				const blob = await response.blob();
				// The blob's own type wins over the hint. A stale hint would be recorded as
				// the new file row's mimeType and then feed FileIsImageOrVideo and the
				// Files-list preview decisions, so the bytes we actually fetched are the
				// better authority; the hint is only a fallback for a typeless blob.
				const mimeType = blob.type || hints?.mimeType || '';
				// The pasted node's own filename, when it has one — without it every adopted
				// file would be named from its mime type alone, which is exactly the
				// information a chip exists to display.
				//
				// But it has to survive validateUploadFile, which uploadBlob runs: a name
				// with no extension is rejected outright. A node whose stored name lost its
				// extension somewhere upstream would otherwise fetch fine, fail validation,
				// toast "Folders and files without types not allowed" at a user who pasted a
				// chip, and drop it from the pasted content. Give it an extension rather than
				// let a bad hint silently delete the paste.
				const extension = mimeType.split('/')[1] || 'bin';
				const hinted = hints?.name?.trim();
				const name = hinted ? (hinted.includes('.') ? hinted : `${hinted}.${extension}`) : `file.${extension}`;
				return await uploadBlob(new File([blob], name, {type: mimeType}));
			} catch {
				return null;
			}
		},
	};
};

/**
 * Hook form, for function components.
 *
 * Memoised on `taskId` alone. The getter is reached through a ref so that a
 * caller passing an inline arrow — the natural call style — cannot change the
 * adapter's identity, which is the one thing that must not happen: see the
 * factory's note on why keying an adapter on the file set rebuilds the editor
 * mid-edit without even refreshing adoption.
 */
export const useInlineMediaAdapter = (
	taskId: string,
	getOwnFileIds: () => Set<string>,
	intl: IntlShape,
	openFile?: (fileId: string) => void
): InlineMediaAdapter => {
	const getOwnFileIdsRef = useRef(getOwnFileIds);
	getOwnFileIdsRef.current = getOwnFileIds;
	// Reached through a ref for the same reason as the file ids: a caller passing an
	// inline arrow must not change the adapter's identity.
	const openFileRef = useRef(openFile);
	openFileRef.current = openFile;
	// `intl` goes through a ref for the same reason, and the comment below used to claim
	// a liveness the code did not have: react-intl hands back a NEW shape object on a
	// locale change, so an adapter that captured the old one by value kept toasting in
	// the previous locale until something rebuilt it. Read through the ref it is genuinely
	// current at call time.
	const intlRef = useRef(intl);
	intlRef.current = intl;

	// Memoised on taskId alone. `intl` is deliberately not a dependency: react-intl hands
	// back a new shape object on a locale change, which would rebuild the adapter and,
	// through it, the editor's plugin list — re-deserialising the whole document.
	//
	// Everything that can change without a new taskId is therefore reached through a ref
	// AND behind a function, which is the part that matters. `openFileRef.current` passed
	// directly would be read once, while the memo factory runs, and baked in: a caller
	// whose `openFile` is not resolved on first render would get permanently inert chips,
	// with nothing short of a taskId change able to recover them. Wrapping it defers the
	// read to call time, the way `getOwnFileIds` already does.
	return useMemo(
		() =>
			createInlineMediaAdapter(
				taskId,
				() => getOwnFileIdsRef.current(),
				{formatMessage: (descriptor, values) => intlRef.current.formatMessage(descriptor, values)} as IntlShape,
				fileId => openFileRef.current?.(fileId)
			),
		[taskId]
	);
};
